import { FormEvent, useState, useEffect } from 'react'
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { Provider } from '@supabase/supabase-js'
import { supabase } from '../services/supabaseClient'
import Navbar from '../components/Navbar'
import Chatbot from '../components/Chatbot'

// Define a proxy URL for icons that might have CORS issues
const getProxiedIconUrl = (url: string) => {
  // If the URL is from npass.app, use a proxy or a local fallback
  if (url.includes('npass.app')) {
    return '/vite.svg'; // Use a local fallback icon
  }
  return url;
}

export default function LoginPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isGoogleLoading, setIsGoogleLoading] = useState(false)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const { signIn, signInWithProvider } = useAuth()

  // Check for the fresh=true parameter and other error parameters
  useEffect(() => {
    // Check for navigation state messages (from signup/verification)
    const state = location.state as { message?: string; messageType?: 'success' | 'error' } | null
    if (state?.message) {
      if (state.messageType === 'error') {
        setError(state.message)
      } else {
        setSuccess(state.message)
      }
      // Clear the state so message doesn't persist on refresh
      window.history.replaceState({}, document.title)
    }

    // Check for pending invitation
    const pendingInvitation = sessionStorage.getItem('pendingInvitation')
    const invitationEmail = sessionStorage.getItem('invitationEmail')
    const emailParam = searchParams.get('email')

    if (emailParam) {
      setEmail(decodeURIComponent(emailParam))
    } else if (invitationEmail) {
      setEmail(invitationEmail)
    }
    
    // Check for success=password_updated parameter
    const successParam = searchParams.get('success')
    if (successParam === 'password_updated') {
      setSuccess('Your password has been updated. Please sign in with your new password.')
      navigate('/login', { replace: true })
      return;
    }
    
    // Check for fresh=true parameter
    const freshStart = searchParams.get('fresh')
    if (freshStart === 'true') {
      setSuccess('Authentication data has been reset. Please sign in again with the same Supabase project.')
      
      // Force sign out just to be sure
      supabase.auth.signOut()
      
      // Remove the parameter from the URL
      navigate('/login', { replace: true })
      return;
    }
    
    // Check for OAuth error parameters
    const oauthError = searchParams.get('error')
    const messageParam = searchParams.get('message')
    
    if (oauthError === 'oauth_state' && messageParam) {
      // This is forwarded from our special handler for bad_oauth_state
      setError(messageParam)
      
      // Force sign out to be sure
      supabase.auth.signOut()
      
      // Remove the parameter from the URL
      navigate('/login', { replace: true })
      return;
    }
    
    // Check for generic message parameter (used for password reset success)
    if (messageParam && !oauthError) {
      setSuccess(messageParam)
      // Remove the parameter from the URL
      navigate('/login', { replace: true })
      return;
    }
  }, [searchParams, navigate, location.state])
  
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)
    
    try {
      // Clear any previous auth states or flags
      sessionStorage.removeItem('oauth_callback_processed')
      sessionStorage.removeItem('sf_callback_processed')
      localStorage.removeItem('sf_auth_state')
      localStorage.removeItem('sf_auth_user')
      sessionStorage.setItem('auth_redirect_count', '0')
      
      await signIn(email, password)
      
      // Add a small delay to allow auth state to update
      setTimeout(() => {
        // Check for pending invitation
        const pendingInvitation = sessionStorage.getItem('pendingInvitation')
        if (pendingInvitation) {
          navigate(`/invitation/${pendingInvitation}`)
          return
        }
        
        // Check for pending transfer
        const pendingTransferToken = sessionStorage.getItem('pendingTransferToken')
        if (pendingTransferToken) {
          sessionStorage.removeItem('pendingTransferToken')
          navigate(`/accept-transfer?token=${pendingTransferToken}`)
          return
        }
        
        // Check for return URL
        const returnUrl = searchParams.get('returnUrl')
        if (returnUrl) {
          const decodedUrl = decodeURIComponent(returnUrl)
          // Validate it's a relative URL for security
          if (decodedUrl.startsWith('/') && !decodedUrl.startsWith('//')) {
            navigate(decodedUrl)
            return
          }
        }
        
        // Default to dashboard
        navigate('/dashboard')
      }, 500)
    } catch (err) {
      setError('Failed to sign in. Please check your credentials.')
      console.error(err)
    } finally {
      setIsLoading(false)
    }
  }
  
  const handleGoogleSignIn = async () => {
    setError('')
    setIsGoogleLoading(true)
    
    try {
      console.log('Starting Google sign-in process')
      
      // Store return URL in sessionStorage to persist across OAuth redirect
      const returnUrl = searchParams.get('returnUrl')
      if (returnUrl) {
        sessionStorage.setItem('authReturnUrl', returnUrl)
      }
      
      // Using the most direct and reliable approach
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google', 
        options: {
          redirectTo: `${window.location.origin}/auth/callback`
        }
      })
      
      if (error) {
        console.error('Google sign-in error:', error)
        throw error
      }
      
      if (!data?.url) {
        console.error('No redirect URL provided by Supabase')
        throw new Error('No redirect URL received')
      }
      
      console.log('Redirecting to Google authorization page...')
      window.location.href = data.url
    } catch (err) {
      console.error('Failed to initiate Google sign-in:', err)
      setError('Failed to start Google sign-in. Please try again.')
      setIsGoogleLoading(false)
    }
  }

  
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar isPublic={true} />
      <div className="flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">Sign in to your account</h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Or{' '}
            <Link 
              to={searchParams.get('returnUrl') ? `/signup?returnUrl=${searchParams.get('returnUrl')}` : '/signup'} 
              className="font-medium text-blue-600 hover:text-blue-500"
            >
              create a new account
            </Link>
          </p>
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="flex">
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">{error}</h3>
                </div>
              </div>
            </div>
          )}
          
          {success && (
            <div className="rounded-md bg-green-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-green-800">{success}</h3>
                </div>
              </div>
            </div>
          )}
          
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="email-address" className="sr-only">Email address</label>
              <input
                id="email-address"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 bg-white rounded-t-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="password" className="sr-only">Password</label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 bg-white rounded-b-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          </div>
          
          <div className="flex items-center justify-between">
            <div className="text-sm">
              <Link to="/reset-password" className="font-medium text-blue-600 hover:text-blue-500">
                Forgot your password?
              </Link>
            </div>
          </div>
          
          <div>
            <button
              type="submit"
              disabled={isLoading || isGoogleLoading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-300"
            >
              {isLoading ? 'Signing in...' : 'Sign in'}
            </button>
          </div>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-gray-50 text-gray-500">Or continue with</span>
              </div>
            </div>

            <div className="mt-6">
              <div className="space-y-3">
                <button
                  type="button"
                  onClick={handleGoogleSignIn}
                  disabled={isLoading || isGoogleLoading}
                  className="w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                >
                  <svg className="h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
                    <g transform="matrix(1, 0, 0, 1, 27.009001, -39.238998)">
                      <path fill="#4285F4" d="M -3.264 51.509 C -3.264 50.719 -3.334 49.969 -3.454 49.239 L -14.754 49.239 L -14.754 53.749 L -8.284 53.749 C -8.574 55.229 -9.424 56.479 -10.684 57.329 L -10.684 60.329 L -6.824 60.329 C -4.564 58.239 -3.264 55.159 -3.264 51.509 Z" />
                      <path fill="#34A853" d="M -14.754 63.239 C -11.514 63.239 -8.804 62.159 -6.824 60.329 L -10.684 57.329 C -11.764 58.049 -13.134 58.489 -14.754 58.489 C -17.884 58.489 -20.534 56.379 -21.484 53.529 L -25.464 53.529 L -25.464 56.619 C -23.494 60.539 -19.444 63.239 -14.754 63.239 Z" />
                      <path fill="#FBBC05" d="M -21.484 53.529 C -21.734 52.809 -21.864 52.039 -21.864 51.239 C -21.864 50.439 -21.724 49.669 -21.484 48.949 L -21.484 45.859 L -25.464 45.859 C -26.284 47.479 -26.754 49.299 -26.754 51.239 C -26.754 53.179 -26.284 54.999 -25.464 56.619 L -21.484 53.529 Z" />
                      <path fill="#EA4335" d="M -14.754 43.989 C -12.984 43.989 -11.404 44.599 -10.154 45.789 L -6.734 42.369 C -8.804 40.429 -11.514 39.239 -14.754 39.239 C -19.444 39.239 -23.494 41.939 -25.464 45.859 L -21.484 48.949 C -20.534 46.099 -17.884 43.989 -14.754 43.989 Z" />
                    </g>
                  </svg>
                  {isGoogleLoading ? 'Connecting...' : 'Sign in with Google'}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
      </div>
      <Chatbot />
    </div>
  )
}
